import { useMemo } from "react";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { listPropDefs } from "../core/ops";
import type { FilterAst } from "../core/filter";
import type { Level, Task } from "../core/schema";
import type { SignalDef } from "../signals/describe";
import { visibleTasks } from "../views/shared";
import type { FilterSources } from "./fields";

/** The builder's choices from a space's doc, and its live preview (open and done tasks, notes left out). */
export function useFilterSources(handle: DocHandle | null | undefined, level: Level): FilterSources | null {
  const slice = useDocSelector(handle ?? null, (doc) =>
    JSON.stringify({
      propDefs: listPropDefs(doc),
      groups: doc.getMap("groups").toJSON(),
      sequences: doc.getMap("sequences").toJSON(),
      signals: (doc.getMap("settings").toJSON() as { signals?: Record<string, SignalDef> }).signals ?? {},
    }),
  );
  const tasks = useDocSelector(handle ?? null, (doc) =>
    visibleTasks(doc, undefined, Date.now(), { includeDone: true, includeSomeday: true }).tasks.map(
      (x): Task => x,
    ),
  );
  return useMemo(() => {
    if (!slice || !handle) return null;
    const base = JSON.parse(slice) as Omit<FilterSources, "tasks" | "level" | "preview">;
    return {
      ...base,
      tasks: tasks ?? [],
      level,
      preview: (ast: unknown) => {
        const hits = visibleTasks(handle.doc, ast as FilterAst | undefined, Date.now(), {
          includeDone: true,
          includeSomeday: true,
        }).tasks;
        return { count: hits.length, titles: hits.slice(0, 4).map((x) => x.title) };
      },
    };
  }, [slice, tasks, handle, level]);
}
